import React, { useState, Dispatch, SetStateAction } from 'react';
import { DocumentGroup, DocumentInfo } from 'types/documentGroup';
import { openErrorNotification } from 'hyarchis-global';
import produce from 'immer';
import styled from 'styled-components';
import Collapsible from 'react-collapsible';
import poll from 'services/poller';
import { JobOutput } from 'types';
import ProcessingJobRepository from 'services/api-repositories/processingJobs/ProcessingJobRepository';
import GroupListItemHead from './GroupListItemHead';
import GroupListItemBody from './GroupListItemBody';
import DocumentGroupRepository from '../../../services/api-repositories/documentsGroups/DocumentGroupRepository';
import { DataMatchGroup, JobCrossMatch, ProcessingJob } from '../../../types/proccesingJobs';
import GroupListItemMatches from './GroupListItemMatches';

interface OwnProps {
  documentGroup: DocumentGroup;
  allDocuments: JobOutput[];
  onRemove: (groupId: string) => void;
  onUpdate: (documentGroup: DocumentGroup) => void;
  setDocuments: Dispatch<SetStateAction<JobOutput[]>>;
}

const GroupItem = styled.div`
  border: 1px solid rgb(224, 224, 224);
  border-radius: 5px;
  margin-bottom: 2px;
  margin-right: 10px;
`;

const GroupListItem = ({
  documentGroup,
  allDocuments,
  setDocuments,
  onRemove,
  onUpdate,
}: OwnProps) => {
  const defaultJobCrossMatch: JobCrossMatch[] = [];
  const [collapseFlag, setCollapseFlag] = useState(false);
  const [matchCollapseFlag, setMatchCollapseFlag] = useState(false);
  const [selectedCrossMatches, setSelectedCrossMatches] = useState(defaultJobCrossMatch);

  const groupDocuments = allDocuments.filter(
    (doc) => doc.documentsGroupId && doc.documentsGroupId === documentGroup.id,
  );

  const handleRemoveDocument = async (groupId: string, documentId: string) => {
    setDocuments((docs) =>
      docs.filter((doc) => doc.documentsGroupId !== groupId || doc.documentId !== documentId),
    );

    const documentToRemoveIndex = allDocuments.findIndex(
      (doc) => doc.documentsGroupId === groupId && doc.documentId === documentId,
    );

    if (documentToRemoveIndex) {
      await DocumentGroupRepository.removeDocumentFromGroup(groupId, documentId);
    }
  };

  const handleProcessGroup = async (
    group: DocumentGroup,
    source: string | undefined,
    documentIds: string[] | undefined,
  ) => {
    try {
      if (!group.groupWorkflow?.id) {
        return;
      }
      let validDocumentIds;
      if (documentIds !== undefined) {
        validDocumentIds = documentIds.filter((id) => id.trim() !== '');
      }

      const processingJob = await ProcessingJobRepository.createGroupProcess(
        group.id,
        group.groupWorkflow?.id,
        source,
        validDocumentIds,
      );
      const updatedGroup = { ...group };
      updatedGroup.processingJob = processingJob;

      onUpdate(updatedGroup);

      const finishedJob = await poll<ProcessingJob>(
        () => ProcessingJobRepository.get(processingJob.processingJobId),
        (fetchedJob) => fetchedJob.status === 'inProgress',
      );
      updatedGroup.processingJob = finishedJob;
      onUpdate(updatedGroup);
    } catch (error) {
      const jobError = (error as Error).message;
      openErrorNotification(jobError);
    }
  };

  const handleRefreshDocuments = async (group: DocumentGroup) => {
    const { documents } = await DocumentGroupRepository.get(group.id);

    setDocuments((docs) =>
      docs.filter(
        (doc) =>
          doc.documentsGroupId !== group.id ||
          doc.job === undefined ||
          documents.find((document) => doc.job?.processingJobId === document.processingJobId),
      ),
    );

    for (const document of documents) {
      refreshDocument(document, group);
    }

    setCollapseFlag(true);
  };

  const refreshDocument = async (document: DocumentInfo, group: DocumentGroup) => {
    if (!document || !document.processingJobId) {
      return;
    }

    const processingJob = await ProcessingJobRepository.get(document.processingJobId);
    let updated = true;

    setDocuments((docs) => {
      const existingDocuments = produce(docs, (jobsDraft) => {
        const jobDraft = jobsDraft.find(
          (j) => j.processingJobId === document.processingJobId && j.documentsGroupId === group.id,
        );
        if (jobDraft) {
          jobDraft.job = processingJob;
          jobDraft.documentId = document.documentId;
        } else {
          updated = false;
        }
      });

      if (!updated) {
        const jobOutput: JobOutput = {
          job: processingJob,
          documentsGroupId: group.id,
          skipProcessing: group.documentWorkflow?.id?.length === 0,
          documentId: document.documentId,
        };
        return [...existingDocuments, jobOutput];
      }

      return existingDocuments;
    });
  };

  const updateJobOutput = (jobOutput: JobOutput) => {
    setDocuments((existingJobs) => {
      const newItems = produce(existingJobs, (jobsDraft) => {
        const jobDraft = jobsDraft.find((j) => j.file && j.documentId === jobOutput.documentId)!;
        if (jobDraft) {
          jobDraft.documentsGroupId = jobOutput.documentsGroupId;
          jobDraft.job = jobOutput.job;
          jobDraft.file = jobOutput.file;
        }
      });
      setCollapseFlag(true);
      if (!jobOutput.job) {
        return [...newItems, jobOutput];
      }

      return newItems;
    });
  };

  const onToggleMatches = (isSet: boolean, matchGroup: DataMatchGroup) => {
    const dataSets = documentGroup?.processingJob?.output?.crossMatchResult?.dataset ?? [];
    const jobCrossMatches: JobCrossMatch[] = [];

    matchGroup.dataSetReferenceIndices.forEach((index) => {
      const jobCrossMatch: JobCrossMatch = {
        dataType: matchGroup.dataType,
        value: matchGroup.value,
        matchType: matchGroup.matchType,
        documentId: dataSets[index].documentDataSource.documentGroupDocumentId,
      };

      jobCrossMatches.push(jobCrossMatch);
    });

    if (isSet) {
      setSelectedCrossMatches([...selectedCrossMatches, ...jobCrossMatches]);
    } else {
      const remainingSelectedCrossMatches = selectedCrossMatches.filter(
        (match) =>
          jobCrossMatches.findIndex(
            (jobMatch) =>
              jobMatch.documentId === match.documentId && jobMatch.dataType === match.dataType,
          ) < 0,
      );
      setSelectedCrossMatches([...remainingSelectedCrossMatches]);
    }
  };

  const toggleCollapse = () => {
    setCollapseFlag(!collapseFlag);
  };

  const toggleMatchCollapse = () => {
    setMatchCollapseFlag(!matchCollapseFlag);
  };

  return (
    <GroupItem>
      <GroupListItemHead
        key={documentGroup.id}
        documentGroup={documentGroup}
        collapsed={!collapseFlag}
        matchCollapsed={!matchCollapseFlag}
        onCollapseClick={toggleCollapse}
        onMatchCollapseClick={toggleMatchCollapse}
        onRefresh={handleRefreshDocuments}
        onProcessGroup={handleProcessGroup}
        onRemove={onRemove}
        setOutput={updateJobOutput}
      />
      <Collapsible trigger='' open={matchCollapseFlag}>
        <GroupListItemMatches group={documentGroup} onToggleMatches={onToggleMatches} />
      </Collapsible>
      <Collapsible trigger='' open={collapseFlag}>
        <GroupListItemBody
          group={documentGroup}
          documents={groupDocuments}
          remove={handleRemoveDocument}
          jobCrossMatches={selectedCrossMatches}
        />
      </Collapsible>
    </GroupItem>
  );
};

export default React.memo(GroupListItem);
